import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Collapse,
  Form,
  Button,
  Input,
  message,
  Tabs,
  Modal,
  Alert,
  Spin,
} from "antd";
import {
  CaretRightOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageHeader from "../Layout/PageHeader";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { TabPane } = Tabs;

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ms", label: "Malay" },
  { code: "zh", label: "Chinese" },
];

// Alert Banner is plain marketing copy — only inline emphasis and links, no headings/lists/images.
const quillModules = {
  toolbar: [["bold", "italic", "link"]],
};

const quillFormats = ["bold", "italic", "link"];

const AlertBanner = () => {
  const { permissions } = usePermissions();
  const canUpdate = permissions.includes(PERMISSIONS.UPDATE_ALERT_BANNER);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState({
    fetch: false,
    save: false,
    disable: false,
  });

  // Load the current active alert per language and pre-fill the editor.
  const fetchActiveAlerts = useCallback(async () => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const res = await axios.get("/api/announcements", {
        params: { type: "alert", isAnnouncement: true, limit: 100, page: 1 },
      });
      const alerts = res.data?.data || [];

      const values = {};
      for (const { code } of LANGUAGES) {
        // getAnnouncement() returns the newest active alert per language, so the
        // first match here mirrors what the homepage banner actually shows.
        const active = alerts.find((a) => a.lang === code && a.isAnnouncement);
        values[`title_${code}`] = active?.title || "";
        values[`text_${code}`] = active?.text || "";
      }
      form.setFieldsValue(values);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  }, [form]);

  useEffect(() => {
    fetchActiveAlerts();
  }, [fetchActiveAlerts]);

  const submit = async (payload) => {
    const key = payload.isAnnouncement ? "save" : "disable";
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await axios.put("/api/announcements/alert-banner", payload);
      message.success(
        payload.isAnnouncement
          ? "Alert Banner updated. All other alerts have been disabled."
          : "Alert Banner disabled. No banner is active.",
      );
      fetchActiveAlerts();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveHandler = (values) => {
    const missing = [];
    const payload = { isAnnouncement: true };
    for (const { code, label } of LANGUAGES) {
      const title = values[`title_${code}`]?.trim();
      const text = values[`text_${code}`]?.trim();
      if (!title || !text) {
        missing.push(label);
        continue;
      }
      payload[code] = { title, text };
    }

    if (missing.length > 0) {
      message.error(
        `All 3 languages are required. Missing: ${missing.join(", ")}`,
      );
      return;
    }

    Modal.confirm({
      title: "Activate this Alert Banner?",
      icon: <ExclamationCircleOutlined />,
      content:
        "The 3 alerts below will become active. All other alert banners across every language will be disabled. Continue?",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => submit(payload),
    });
  };

  const disableHandler = () => {
    Modal.confirm({
      title: "Disable all Alert Banners?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Every alert banner across all languages will be disabled. No banner will be shown on the homepage. Continue?",
      okText: "Disable",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => submit({ isAnnouncement: false }),
    });
  };

  return (
    <div>
      <PageHeader
        title="Alert Banner"
        childRoutes={[
          { path: "/alert-banner", breadcrumbName: "Alert Banner" },
        ]}
      />

      <Alert
        type="info"
        showIcon
        className="mb-2"
        message="Homepage Alert Banner"
        description="Alert Banner shown on Partner Dashboard homepage, where we usually share urgent or ad hoc notices, such as service disruptions."
      />

      <Collapse
        defaultActiveKey={["alert-banner"]}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Edit Alert Banner" key="alert-banner">
          <Spin spinning={loading.fetch}>
            <Form form={form} layout="vertical" onFinish={saveHandler}>
              <Tabs defaultActiveKey="en" type="card">
                {LANGUAGES.map(({ code, label }) => (
                  <TabPane tab={label} key={code}>
                    <Form.Item
                      name={`title_${code}`}
                      label={`Title (${code.toUpperCase()})`}
                      rules={[
                        {
                          required: true,
                          message: `Title (${label}) is required`,
                        },
                      ]}
                    >
                      <Input
                        placeholder={`Alert banner title (${label})`}
                        disabled={!canUpdate}
                      />
                    </Form.Item>
                    <Form.Item
                      name={`text_${code}`}
                      label={`Text (${code.toUpperCase()})`}
                      rules={[
                        {
                          required: true,
                          message: `Text (${label}) is required`,
                        },
                      ]}
                    >
                      <ReactQuill
                        theme="snow"
                        readOnly={!canUpdate}
                        modules={quillModules}
                        formats={quillFormats}
                        style={{ height: "200px", marginBottom: "50px" }}
                      />
                    </Form.Item>
                  </TabPane>
                ))}
              </Tabs>

              {canUpdate && (
                <Row gutter={8}>
                  <Col>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading.save}
                    >
                      Save & Activate
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      danger
                      onClick={disableHandler}
                      loading={loading.disable}
                    >
                      Disable Banner
                    </Button>
                  </Col>
                </Row>
              )}
            </Form>
          </Spin>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default AlertBanner;
